"use client";

import {
    Anchor,
    Button,
    Center,
    Grid,
    Group,
    Loader,
    Notification,
    rem,
    SegmentedControl,
    Stack,
    Title,
    Tooltip,
    useMantineColorScheme,
    useMantineTheme
} from "@mantine/core";
import {PricingPlanWidget} from "@/components/pricing/PricingPlanWidget";
import {Span} from "@/components/common/Span";
import {Suspense, useCallback, useEffect, useMemo, useState} from "react";
import {BillingCycle, BillingCycles, CustomerCreditBalance} from "@/app/lib/pricing/types";
import {IconArrowLeft, IconArrowUp, IconHeartDiscount} from "@tabler/icons-react";
import {useMobile, useShortScreen, useTiltedMobile} from "@/components/common/hooks/responsive-utils";
import {Accent} from "@/components/common/Accent";
import {usePricing} from "@/components/pricing/use-pricing";
import {usePrimaryColor, useSlightlyDimmedColor} from "@/components/common/hooks/useColors";
import {Emp} from "@/components/common/Emp";
import {useRouter} from "next/navigation";
import {APP_URL, MARKETING_URL, SUPPORT_EMAIL} from "@/common/utils";
import {getPlanDetails} from "@/app/lib/pricing/utils";
import Image from "next/image";
import {useAffiliate} from "@/components/common/hooks/useAffiliate";
import {useDynamicPricingPlans} from "@/components/pricing/use-dynamic-pricing-plans";
import {useProfile} from "@/components/common/hooks/useProfile";
import {usePaddle} from "@/components/pricing/use-paddle";
import {CheckoutCustomer, CheckoutEventNames} from "@paddle/paddle-js";
import {PricingPlan} from "@/components/pricing/PricingPlan";
import {PaddleEventData} from "@paddle/paddle-js/types/checkout/checkout";
import ToltScript from "@/app/(layout)/pricing/ToltScript";
import TrackAffiliate from "@/components/common/TrackAffiliate";
import * as Sentry from "@sentry/nextjs";
import {formatBalance} from "@/app/lib/pricing/pricing-plans";
import {useKeyEvents} from "@/components/common/hooks/useKeyEvents";

export default function PricingPage() {
    const router = useRouter();

    const titleSize = 40;
    const subTitleSize = 18;

    const checkoutConfirmationTitleSize = 18;
    const checkoutConfirmationSize = 20;

    const {
        authClient,
        reloadProfile,
    } = useProfile();

    const isMobile = useMobile();
    const isTilted = useTiltedMobile();
    const isShort = useShortScreen();

    // console.log('mobile', isMobile, 'isTilted', isTilted, 'isShort', isShort);

    const theme = useMantineTheme();
    const {colorScheme: scheme} = useMantineColorScheme();
    const discountColor = theme.colors[theme.primaryColor][scheme === 'light' ? 5 : 5];
    const primaryColor = usePrimaryColor();
    const dimmed = useSlightlyDimmedColor();

    const [billing, setBilling] = useState<BillingCycle>('monthly');
    const onChangeBilling = useCallback((value: string) => {
        setBilling(value as BillingCycle);
    }, []);

    const {loadingPrices, plans} = useDynamicPricingPlans();

    const affiliate = useAffiliate();

    const pricingDetails = usePricing(affiliate);

    const { gaCheckout, gaAbandon, gaPurchase } = useKeyEvents();

    const {
        user,
        profile,
        loggedIn,
        wantedPlan,
        wantedKind,
        wantedBilling,
        existingPlan,
    } = pricingDetails;

    const [requestedCreditBalances, setRequestedCreditBalances] = useState(false);
    const [creditBalances, setCreditBalances] = useState<CustomerCreditBalance[]>([]);
    const [loadingCreditBalances, setLoadingCreditBalances] = useState(false);
    const [creditBalanceError, setCreditBalanceError] = useState<Error | null>(null);
    useEffect(() => {
        if (profile == null || profile.subscription == null || profile.subscription.customerId == null) {
            return;
        }
        if (requestedCreditBalances || loadingCreditBalances) {
            return;
        }
        setRequestedCreditBalances(true);
        setLoadingCreditBalances(true);
        pricingDetails.getCreditBalance()
            .then(creditBalances => {
                setCreditBalances(creditBalances.filter(cb => cb.availableBalance > 0));
                setLoadingCreditBalances(false);
            })
            .catch((error) => {
                Sentry.captureException(error);
                setCreditBalanceError(error instanceof Error ? error : new Error(String(error)));
                setLoadingCreditBalances(false);
            });
    }, [loadingCreditBalances, pricingDetails, pricingDetails.getCreditBalance, profile, requestedCreditBalances]);

    // this will only be displayed if the user has a cancelled subscription and is back to buy a new one
    const creditBalanceNotice = useMemo(() => {
        if (profile == null ||
            profile.subscription == null ||
            !profile.subscription.isCancelled ||
            !requestedCreditBalances ||
            loadingCreditBalances) {
            return null;
        }

        if (creditBalanceError != null) {
            return (
                <Notification withBorder
                              withCloseButton={false}
                              bg={'var(--mantine-color-body)'}
                              variant={'outline'}
                              color={'rougeRed'}
                              py={'md'}
                              my={'md'}
                              title={<Span fz={checkoutConfirmationTitleSize}>You have available credit</Span>}>
                    <Stack gap={isMobile ? 'sm' : 'md'} pt={'xs'} align={'flex-start'}>
                        <Span fz={'md'}>An error occurred while trying to retrieve
                            {' '}your credit balance. Please try again later.
                        </Span>
                        <Span fz={'md'}>
                            If the error persists, please email
                            {' '}<Anchor inherit href={`mailto:${SUPPORT_EMAIL}`}
                                         target={'_blank'}>{SUPPORT_EMAIL}</Anchor>.
                        </Span>
                    </Stack>
                </Notification>
            )
        }

        if (creditBalances.length === 0) {
            return null;
        }

        const balances = creditBalances.map((creditBalance, index) => (
            <Span fz={'md'} c={dimmed} key={index}>
                <Emp>{formatBalance(creditBalance.availableBalance)}</Emp>
                {' '}{creditBalance.currencyCode}
            </Span>
        ));
        return (
            <Notification withBorder
                          withCloseButton={false}
                          bg={'var(--mantine-color-body)'}
                          variant={'outline'}
                          color={'indigo'}
                          py={'md'}
                          my={'md'}
                          title={<Span fz={checkoutConfirmationTitleSize}>You have available credit</Span>}>
                <Stack gap={isMobile ? 'sm' : 'md'} pt={'xs'} align={'flex-start'}>
                    <Span fz={'md'}>There is credit available on your account. It will be applied to your
                        {' '}next subscription if your payment currency is the same as your credit currency.
                    </Span>
                    <Stack gap={'xs'}>
                        <Span fz={'md'}>Available credit:</Span>
                        {balances}
                    </Stack>
                </Stack>
            </Notification>
        );

    }, [creditBalanceError, creditBalances, dimmed, isMobile, loadingCreditBalances, profile, requestedCreditBalances]);

    const [checkedOutPriceId, setCheckedOutPriceId] = useState<string | undefined>(undefined);
    const [checkoutError, setCheckoutError] = useState<Error | undefined>(undefined);
    const [discountId, setDiscountId] = useState<string | null>(null);

    const onCancelCheckout = useCallback(() => {
        // GA-Start
        if (checkedOutPriceId != null) {
            gaAbandon(checkedOutPriceId, affiliate);
        }
        // GA-End
        const discountToRemoveId = discountId;
        if (discountToRemoveId != null) {
            setCheckoutError(undefined);
            setDiscountId(null);
            pricingDetails.removeDiscount(discountToRemoveId)
                .then(() => {
                })
                .catch(() => {
                });
        }
    }, [affiliate, checkedOutPriceId, discountId, gaAbandon, pricingDetails]);

    const [checkoutCompleted, setCheckoutCompleted] = useState(false);

    const onCheckoutCompleted = useCallback(() => {
        // GA-Start
        if (checkedOutPriceId != null) {
            gaPurchase(checkedOutPriceId, affiliate);
        }
        // GA-End
        setCheckoutCompleted(true);
        setDiscountId(null);
        reloadProfile();
    }, [affiliate, checkedOutPriceId, gaPurchase, reloadProfile]);

    // !TODO! in the home page, add animation around the "Launch app" button
    const onCheckoutClosed = useCallback(() => {
        if (checkoutCompleted) {
            setTimeout(() => {
                router.push(MARKETING_URL);
            }, 1000);
        } else {
            onCancelCheckout();
        }
    }, [checkoutCompleted, onCancelCheckout, router]);

    const onPaddleEvent = useCallback((event: PaddleEventData) => {
        if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
            onCheckoutCompleted();
        } else if (event.name === CheckoutEventNames.CHECKOUT_CLOSED) {
            onCheckoutClosed();
        }
    }, [onCheckoutCompleted, onCheckoutClosed]);

    const {
        paddle,
        loading: loadingPaddle,
        error: errorPaddle
    } = usePaddle(onPaddleEvent);

    useEffect(() => {
        document.title = `FreeLimbs | Pricing`;
        document.body.scroll(0, 0);
    }, []);

    const [showCheckoutConfirmation, setShowCheckoutConfirmation] = useState(false);
    const [wantedPlanDetails, setWantedPlanDetails] = useState<ReturnType<typeof getPlanDetails> | undefined>(undefined);

    useEffect(() => {
        if (loggedIn && existingPlan == null && wantedPlan != null && wantedKind != null && wantedBilling != null) {
            setWantedPlanDetails(getPlanDetails(wantedPlan, wantedKind, wantedBilling));
            setShowCheckoutConfirmation(true);
        }
    }, [existingPlan, loggedIn, wantedBilling, wantedKind, wantedPlan]);

    const onCheckout = useCallback(async (plan: PricingPlan, billing: BillingCycle) => {
        setCheckedOutPriceId(undefined);
        if (user == null) {
            const url = `/auth/sign-up?plan=${plan.id}kind=${plan.kind}&billing=${billing}`;
            // no need to pass the affiliate, it will propagate automatically
            router.push(url);
            return;
        }
        setCheckoutError(undefined);
        try {
            const discountResult = await pricingDetails.createDiscount(affiliate);
            let discountId: string | null = null;
            if (discountResult instanceof Error) {
                // ignore discount error
            } else {
                discountId = discountResult.discountId;
                setDiscountId(discountId);
            }
            let customer: CheckoutCustomer | undefined = undefined;
            if (user.email != null) {
                customer = {email: user.email};
            }
            let toltReferral: string | undefined = undefined;
            if (affiliate != null && !affiliate.isCustom) {
                toltReferral = affiliate.affiliateId;
            }
            const priceId = billing === BillingCycles.monthly ? plan.monthlyPriceId : plan.annualPriceId;
            setCheckedOutPriceId(priceId);
            // GA-Start
            gaCheckout(priceId, affiliate);
            // GA-End
            paddle!.Checkout.open({
                discountId,
                customer: customer,
                customData: {
                    user_id: user.id,
                    affiliate,
                    tolt_referral: toltReferral
                },
                items: [
                    {
                        quantity: 1,
                        priceId,
                    }
                ],
                settings: {
                    theme: 'light' // TODO: scheme === 'light' ? 'light' : 'dark',
                }
            })
        } catch (e) {
            const error = e instanceof Error ? e : new Error(String(e));
            setCheckoutError(error);
        }
    }, [affiliate, gaCheckout, paddle, pricingDetails, router, user]);

    const removeWantedPlan = useCallback(async () => {
        if (user == null) {
            return;
        }
        try {
            await authClient.auth.updateUser({
                data: {
                    ...(user.user_metadata ?? {}),
                    wantedPlan: null,
                    wantedKind: null,
                    wantedBilling: null
                }
            });
        } catch (e) {
            console.error('Failed to remove wanted plan', e);
        }
    }, [authClient.auth, user]);

    const onCheckoutWantedPlan = useCallback(async () => {
        const id = wantedPlan!;
        const kind = wantedKind!;
        const billing = wantedBilling!;
        await removeWantedPlan();
        setShowCheckoutConfirmation(false);

        const checkoutPlan = plans
            .flatMap(({plan, planPlus}) => [plan, planPlus])
            .find(p => p.id === id && p.kind === kind)!;

        await onCheckout(checkoutPlan, billing);
    }, [onCheckout, plans, removeWantedPlan, wantedBilling, wantedKind, wantedPlan]);

    const onCancelWantedPlan = useCallback(async () => {
        await removeWantedPlan();
        setShowCheckoutConfirmation(false);
    }, [removeWantedPlan]);

    const onSignupWithPlan = useCallback((plan: PricingPlan, billing: BillingCycle) => {
        router.push(`/auth/sign-up?plan=${plan.id}&kind=${plan.kind}&billing=${billing}`);
    }, [router]);

    const onViewSubscription = useCallback(() => {
        router.push(`${APP_URL}/subscription`);
    }, [router]);

    const subscriptionError = pricingDetails?.subscriptionError ?? false;
    const planProps = useMemo(() => {
        return {
            subscriptionError,
            pricingDetails,
            onCheckout,
            onSignupWithPlan,
            loadingPrices,
            loadingPaymentSystem: loadingPaddle,
            paymentSystemReady: paddle != null,
        };
    }, [loadingPaddle, loadingPrices, onCheckout, onSignupWithPlan, paddle, pricingDetails, subscriptionError]);

    return (
        <Center px={'xs'}>
            <Stack gap={(isMobile || isShort) ? 'md' : 'lg'} maw={isTilted ? '85%' : 1280} align={'center'}>
                <Stack gap={(isMobile || isShort) ? 'sm' : 'sm'}>
                    <Group pt={'md'} gap={'sm'} align={'center'} justify={'center'} wrap={'nowrap'}>
                        <Title order={1} fz={titleSize} ff={theme.headings.fontFamily} fw={'bold'}
                               ta={'center'}>
                            Pricing plans
                        </Title>
                        {pricingDetails.loadingProfile && (
                            <Tooltip label={'Loading profile...'}>
                                <Loader size={titleSize / 2 + titleSize / 10}/>
                            </Tooltip>
                        )}
                        {!pricingDetails.loadingProfile && loadingPrices && (
                            <Tooltip label={'Loading prices, default prices shown'}>
                                <Loader size={titleSize / 2 + titleSize / 10}/>
                            </Tooltip>
                        )}
                    </Group>
                    {(profile != null && profile.isFree) && (
                        <Notification withBorder
                                      withCloseButton={false}
                                      bg={'var(--mantine-color-body)'}
                                      variant={'filled'}
                                      color={theme.colors.indigo[6]}
                                      py={'md'}
                                      my={'md'}
                                      title={<Span fz={checkoutConfirmationTitleSize}>Welcome, friend!</Span>}>
                            <Stack gap={isMobile ? 'sm' : 'md'} pt={'xs'}>
                                <Span fz={'md'}>
                                    You were one of the first to join FreeLimbs, and helped us shape the app.
                                </Span>
                                <Span fz={'md'}>
                                    You have a <Emp>forever-free</Emp> subscription to FreeLimbs.
                                </Span>
                            </Stack>
                        </Notification>
                    )}
                    {!pricingDetails.isOnline && (
                        <Notification withBorder
                                      withCloseButton
                                      bg={'var(--mantine-color-body)'}
                                      variant={'filled'}
                                      color={primaryColor}
                                      py={'md'}
                            // maw={isMobile ? undefined : 500}
                                      title={<Span fz={checkoutConfirmationTitleSize}>You seem to be offline</Span>}>
                            <Stack gap={isMobile ? 'sm' : 'md'} pt={'xs'}>
                                <Span fz={checkoutConfirmationSize}>
                                    Please check your internet connection. You might not be able to purchase a
                                    subscription.
                                </Span>
                            </Stack>
                        </Notification>
                    )}
                    {errorPaddle && (
                        <Notification withBorder
                                      withCloseButton={false}
                                      bg={'var(--mantine-color-body)'}
                                      variant={'filled'}
                                      color={primaryColor}
                                      py={'md'}
                                      maw={isMobile ? undefined : 500}
                                      title={<Span fz={checkoutConfirmationTitleSize}>Could not connect to payment
                                          {' '}service</Span>}>
                            <Stack gap={isMobile ? 'sm' : 'md'} pt={'xs'}>
                                <Span fz={checkoutConfirmationSize}>
                                    An error occurred while trying to connect to our payment provider, Paddle.
                                    {' '}Please refresh the page and try again a bit later.
                                </Span>
                                <Span fz={checkoutConfirmationSize}>
                                    {' '}If the error persists, please email
                                    {' '}<Anchor inherit href={`mailto:${SUPPORT_EMAIL}`}
                                                 target={'_blank'}>{SUPPORT_EMAIL}</Anchor>.
                                </Span>
                            </Stack>
                        </Notification>
                    )}
                    {subscriptionError && (
                        <Notification withBorder
                                      withCloseButton={false}
                                      bg={'var(--mantine-color-body)'}
                                      variant={'filled'}
                                      color={primaryColor}
                                      py={'md'}
                                      maw={isMobile ? undefined : 500}
                                      title={<Span fz={checkoutConfirmationTitleSize}>Could not retrieve your
                                          subscription information</Span>}>
                            <Stack gap={isMobile ? 'sm' : 'md'} pt={'xs'}>
                                <Span fz={checkoutConfirmationSize}>
                                    An error occurred while trying to retrieve your current subscription.
                                    {' '}Please refresh the page a bit later.
                                </Span>
                                <Span fz={checkoutConfirmationSize}>
                                    {' '}If the error persists, please email
                                    {' '}<Anchor inherit href={`mailto:${SUPPORT_EMAIL}`}
                                                 target={'_blank'}>{SUPPORT_EMAIL}</Anchor>.
                                </Span>
                            </Stack>
                        </Notification>
                    )}
                    {checkoutError && (
                        <Notification withBorder
                                      withCloseButton={false}
                                      bg={'var(--mantine-color-body)'}
                                      variant={'filled'}
                                      color={primaryColor}
                                      py={'md'}
                                      maw={isMobile ? undefined : 500}
                                      title={<Span fz={checkoutConfirmationTitleSize}>Checkout error</Span>}>
                            <Stack gap={isMobile ? 'sm' : 'md'} pt={'xs'}>
                                <Span fz={checkoutConfirmationSize}>
                                    An error occurred while trying to create a checkout for your subscription.
                                    {' '}Please try again later.
                                </Span>
                                <Span fz={checkoutConfirmationSize}>
                                    {' '}If the error persists, please email
                                    {' '}<Anchor inherit href={`mailto:${SUPPORT_EMAIL}`}
                                                 target={'_blank'}>{SUPPORT_EMAIL}</Anchor>.
                                    Technical details: {checkoutError.message}
                                </Span>
                                <Center>
                                    <Button variant={'outline'} size={isMobile ? 'sm' : 'md'} px={'xl'}
                                            onClick={() => setCheckoutError(undefined)}>
                                        Close
                                    </Button>
                                </Center>
                            </Stack>
                        </Notification>
                    )}
                    {showCheckoutConfirmation && (
                        <Notification withBorder
                                      withCloseButton={false}
                                      bg={'var(--mantine-color-body)'}
                                      variant={'outline'}
                                      color={primaryColor}
                                      py={'md'}
                                      title={<Span fz={checkoutConfirmationTitleSize}>Complete your purchase</Span>}>
                            <Stack gap={isMobile ? 'sm' : 'md'} pt={'xs'} align={'flex-start'}>
                                <Span fz={checkoutConfirmationSize} c={dimmed}>
                                    You signed up to purchase a
                                    {' '}<Emp>{wantedPlanDetails!.fullName}</Emp> subscription.
                                </Span>
                                <Group gap={'md'} align={'center'} justify={'center'} w={'100%'}>
                                    <Button variant={'filled'} size={isMobile ? 'sm' : 'md'}
                                            onClick={onCheckoutWantedPlan}>
                                        Checkout now
                                    </Button>
                                    <Button variant={'outline'} size={isMobile ? 'sm' : 'md'}
                                            onClick={onCancelWantedPlan}>
                                        Later
                                    </Button>
                                </Group>
                            </Stack>
                        </Notification>
                    )}
                    {pricingDetails.profile?.subscription != null && !pricingDetails.profile.subscription.isCancelled && (
                        <Notification withBorder
                                      withCloseButton={false}
                                      bg={'var(--mantine-color-body)'}
                                      variant={'outline'}
                                      color={'indigo'}
                                      py={'md'}
                                      my={'md'}
                                      title={<Span fz={checkoutConfirmationTitleSize}>You already have
                                          {' '}a subscription</Span>}>
                            <Stack gap={isMobile ? 'sm' : 'md'} pt={'xs'} align={'flex-start'}>
                                <Span fz={'md'} c={dimmed}>
                                    You have a FreeLimbs
                                    {' '}<Emp>{pricingDetails.profile.subscription.name}</Emp> subscription.
                                </Span>
                                <Span fw={'normal'} fz={'sm'}>
                                    You can view, upgrade, downgrade, pause, cancel, or change the billing interval of
                                    {' '}your subscription by clicking the button below.
                                </Span>
                                <Group gap={'md'} align={'center'} justify={'center'} w={'100%'}>
                                    <Button variant={'filled'} size={isMobile ? 'sm' : 'md'}
                                            onClick={onViewSubscription}>
                                        View &amp; manage subscription
                                    </Button>
                                </Group>
                            </Stack>
                        </Notification>
                    )}
                    {creditBalanceNotice != null && creditBalanceNotice}
                    <Center>
                        <Group gap={'sm'} align={'center'} justify={'center'}>
                            <SegmentedControl
                                style={{
                                    marginLeft: isMobile ? undefined : rem(171)
                                }}
                                miw={(isMobile && !isTilted) ? '100%' : 'auto'}
                                orientation={(isMobile && !isTilted) ? 'vertical' : 'horizontal'}
                                fullWidth={isMobile && !isTilted}
                                size={isMobile ? 'sm' : 'md'}
                                data={[
                                    {value: 'monthly', label: 'Monthly billing'},
                                    {
                                        value: 'yearly',
                                        label: 'Yearly billing',
                                    },
                                ]}
                                value={billing}
                                onChange={onChangeBilling}
                            />
                            <Group gap={6} align={'center'} wrap={'nowrap'}>
                                <Group gap={0} align={'center'} wrap={'nowrap'}>
                                    {(isMobile && !isTilted) ?
                                        <IconArrowUp strokeWidth={1.5} color={discountColor}/> :
                                        <IconArrowLeft strokeWidth={1.5} color={discountColor}/>}
                                    <IconHeartDiscount strokeWidth={1.5} color={discountColor}/>
                                </Group>
                                <Accent c={discountColor}
                                        ff={'var(--font-montserrat)'}
                                        fw={700}
                                        style={{userSelect: 'none'}}
                                >2 months OFF</Accent>
                            </Group>
                        </Group>
                    </Center>
                </Stack>
                <Stack gap={'lg'}>
                    <Grid gutter={{base: 16, md: 24}} justify={'center'}>
                        {plans.map(({plan, planPlus}, index) => (
                            <Grid.Col key={index} span={{base: 12, sm: 6, md: 6, lg: 6}}>
                                <PricingPlanWidget plan={plan}
                                                   planPlus={planPlus}
                                                   billing={billing}
                                                   {...planProps}
                                />
                            </Grid.Col>
                        ))}
                    </Grid>
                    <Group gap={'xs'} align={'center'} justify={'center'} mt={'xs'} mb={'lg'}>
                        <Span fz={subTitleSize} ff={theme.headings.fontFamily} c={dimmed} fw={'bold'} ta={'center'}>
                            Secure payments by
                        </Span>
                        <Anchor href={'https://www.paddle.com/'}
                                rel={'noopener noreferrer'}
                                c={'inherit'}
                                fz={1}
                                target={'_blank'}>
                            {scheme === 'light' && (
                                <Image src={'/images/brands/paddle-logo-black-1520x460.svg'}
                                       alt={'Paddle'}
                                       width={80} height={80 * 460 / 1520}/>
                            )}
                            {scheme === 'dark' && (
                                <Image src={'/images/brands/paddle-logo-white-1520x460.svg'}
                                       alt={'Paddle'}
                                       width={80} height={80 * 460 / 1520}/>
                            )}
                        </Anchor>
                    </Group>
                </Stack>
            </Stack>
            {/* affiliates BEGIN */}
            <ToltScript/>
            <Suspense fallback={null}>
                <TrackAffiliate/>
            </Suspense>
            {/* affiliates END */}
        </Center>
    )
}
